import { useMemo, useState } from 'react';
import {
  Archive,
  Download,
  FilterX,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Lock,
  Plus,
  RotateCcw,
  Table as TableIcon,
  User,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import { MemberSelect } from '@/features/members';
import { TagFilterSelect } from '@/features/tags';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { exportCsv, type ApiError } from '@/core/api';
import { canSeeMoney, formatMoney } from '@/lib/money';
import { formatCurrencyTotals, totalsByCurrency } from '@/lib/money-totals';
import { useSession } from '@/stores/session';
import { BASE as DEALS_BASE } from '../api';
import { DEAL_STAGES, stageLabel, stageVariant } from '../constants';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { useArchiveDeal, useDeals, usePurgeDeal, useRestoreDeal } from '../queries';
import type { Deal } from '../types';
import { useDealsUi, type DealsViewMode } from '../store/viewPrefs';

const VIEW_MODES: readonly ViewModeOption<DealsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by stage', icon: Layers },
];

/** Columns the backend lets us order by — keeps typos out of the URL. */
type DealOrderingColumn =
  | 'name'
  | 'stage'
  | 'amount'
  | 'probability'
  | 'expected_close_date'
  | 'created'
  | 'modified';

function MoneyText({
  amount,
  currency,
  visible,
}: {
  amount: string | null;
  currency: string | null;
  visible: boolean;
}) {
  const formatted = formatMoney(amount, currency);
  if (formatted) return <span className="tabular-nums">{formatted}</span>;
  // amount is null: distinguish "masked for this role" (F3) from "no estimate".
  if (!visible) {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-1">
        <Lock className="size-3" /> Restricted
      </span>
    );
  }
  return <span className="text-muted-foreground">—</span>;
}

export function DealsListPage() {
  const navigate = useNavigate();
  const role = useSession(s => s.role);
  const moneyVisible = canSeeMoney(role);
  const view = useDealsUi(s => s.view);
  const setView = useDealsUi(s => s.setView);
  const showArchived = useDealsUi(s => s.showArchived);
  const setShowArchived = useDealsUi(s => s.setShowArchived);
  const ordering = useDealsUi(s => s.ordering);
  const setOrdering = useDealsUi(s => s.setOrdering);
  const mine = useDealsUi(s => s.mine);
  const setMine = useDealsUi(s => s.setMine);
  // Persisted filters (survive reloads via the store). Created is always set
  // (unlike expected_close_date), so the quarter/month period filter narrows rows.
  const stage = useDealsUi(s => s.stage);
  const setStage = useDealsUi(s => s.setStage);
  const owner = useDealsUi(s => s.owner);
  const setOwner = useDealsUi(s => s.setOwner);
  const tag = useDealsUi(s => s.tag);
  const setTag = useDealsUi(s => s.setTag);
  const createdFrom = useDealsUi(s => s.createdFrom);
  const setCreatedFrom = useDealsUi(s => s.setCreatedFrom);
  const createdTo = useDealsUi(s => s.createdTo);
  const setCreatedTo = useDealsUi(s => s.setCreatedTo);

  const [page, setPage] = useState(1);

  const filtersActive =
    stage !== 'all' ||
    owner !== '' ||
    tag !== 'all' ||
    createdFrom !== '' ||
    createdTo !== '' ||
    mine ||
    showArchived;

  function resetFilters() {
    setStage('all');
    setOwner('');
    setTag('all');
    setCreatedFrom('');
    setCreatedTo('');
    setMine(false);
    setShowArchived(false);
    setPage(1);
  }

  const { data, isPending, isError, error, isFetching } = useDeals({
    page,
    stage: stage === 'all' ? undefined : stage,
    archived: showArchived ? true : undefined,
    ordering,
    // Send `mine: true` only when active — backend reads the user, so the
    // client never supplies an id.
    mine: mine ? true : undefined,
    owner: owner ? Number(owner) : undefined,
    created_after: createdFrom || undefined,
    created_before: endOfDayParam(createdTo),
    tag: tag === 'all' ? undefined : Number(tag),
  });

  const archive = useArchiveDeal();
  const restore = useRestoreDeal();
  const purge = usePurgeDeal();

  const results = data?.results ?? [];
  const open = (d: Deal) => navigate(`/crm/deals/${d.public_id}`);
  const onArchive = (d: Deal) => archive.mutate(d.id);
  const onRestore = (d: Deal) => restore.mutate(d.id);
  const onPurge = (d: Deal) => purge.mutate(d.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Deals</h1>
        <Button onClick={() => navigate('/crm/deals/new')}>
          <Plus /> New deal
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={stage}
            onValueChange={v => {
              setPage(1);
              setStage(v);
            }}
          >
            {/* Content-width — sizes to the selected stage, not a fixed box. */}
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {DEAL_STAGES.map(s => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <MemberSelect
            value={owner}
            onChange={v => {
              setPage(1);
              setOwner(v);
            }}
            placeholder="All sales"
            queryKey="deals-owner-filter"
            roles={CRM_OWNER_ROLES}
            clearable
            triggerClassName="h-9 w-auto min-w-28"
          />
          <TagFilterSelect
            value={tag}
            onChange={v => {
              setPage(1);
              setTag(v);
            }}
          />
          <DateRangeFilter
            label="Created"
            idPrefix="deal-created"
            from={createdFrom}
            to={createdTo}
            onFromChange={v => {
              setPage(1);
              setCreatedFrom(v);
            }}
            onToChange={v => {
              setPage(1);
              setCreatedTo(v);
            }}
          />
          {filtersActive ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={resetFilters}
              aria-label="Reset all filters"
              title="Reset all filters"
            >
              <FilterX className="size-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={mine ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setMine(!mine);
            }}
            aria-pressed={mine}
            aria-label={mine ? 'Showing my deals' : 'Show only my deals'}
          >
            <User className="size-4" /> Mine
          </Button>
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setShowArchived(!showArchived);
            }}
            aria-pressed={showArchived}
            aria-label={showArchived ? 'Showing archived deals' : 'Show archived deals'}
            title={showArchived ? 'Showing archived deals' : 'Show archived deals'}
          >
            <Archive className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `deals.csv` without leaving the SPA.
              void exportCsv(DEALS_BASE, {
                stage: stage === 'all' ? undefined : stage,
                archived: showArchived ? true : undefined,
                ordering,
                mine: mine ? true : undefined,
                owner: owner ? Number(owner) : undefined,
                created_after: createdFrom || undefined,
                created_before: endOfDayParam(createdTo),
                tag: tag === 'all' ? undefined : Number(tag),
                export: 'csv',
              });
            }}
            aria-label="Export deals as CSV"
            title="Export deals as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isPending ? (
        <DealsTable
          deals={[]}
          onRowClick={() => undefined}
          moneyVisible={moneyVisible}
          loading
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : results.length === 0 ? (
        // Empty state is consistent across views: render the table chrome with
        // an inline "No deals found." row, regardless of selected view mode.
        <DealsTable
          deals={[]}
          onRowClick={() => undefined}
          moneyVisible={moneyVisible}
          emptyMessage={showArchived ? 'No archived deals.' : undefined}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <DealsTable
          deals={results}
          onRowClick={open}
          moneyVisible={moneyVisible}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <DealsList deals={results} onOpen={open} moneyVisible={moneyVisible} />
      ) : view === 'cards' ? (
        <DealsCards deals={results} onOpen={open} moneyVisible={moneyVisible} />
      ) : (
        <DealsGroupedByStage deals={results} onOpen={open} moneyVisible={moneyVisible} />
      )}

      {/* Pipeline totals — visible only on the current page of results, since
          we don't have a server-side aggregate endpoint yet. Per-currency
          buckets (F4) — Deals don't snapshot base USD. */}
      {moneyVisible && results.length > 0 ? (
        <p className="text-muted-foreground text-sm">
          Page total: {formatCurrencyTotals(totalsByCurrency(results))}
        </p>
      ) : null}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'deal' : 'deals'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ViewProps {
  deals: Deal[];
  onOpen: (d: Deal) => void;
  moneyVisible: boolean;
}

interface DealsTableProps {
  deals: Deal[];
  onRowClick: (d: Deal) => void;
  moneyVisible: boolean;
  loading?: boolean;
  emptyMessage?: string;
  onArchive: (d: Deal) => void;
  onRestore: (d: Deal) => void;
  onPurge: (d: Deal) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function DealsTable({
  deals,
  onRowClick,
  moneyVisible,
  loading = false,
  emptyMessage,
  onArchive,
  onRestore,
  onPurge,
  ordering,
  onOrderingChange,
}: DealsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<DealOrderingColumn>
              column="name"
              value={ordering}
              onChange={onOrderingChange}
            >
              Name
            </SortableTableHead>
            <SortableTableHead<DealOrderingColumn>
              column="stage"
              value={ordering}
              onChange={onOrderingChange}
            >
              Stage
            </SortableTableHead>
            <SortableTableHead<DealOrderingColumn>
              column="amount"
              value={ordering}
              onChange={onOrderingChange}
            >
              Value
            </SortableTableHead>
            <SortableTableHead<DealOrderingColumn>
              column="probability"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Probability
            </SortableTableHead>
            <SortableTableHead<DealOrderingColumn>
              column="expected_close_date"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Expected close
            </SortableTableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                {Array.from({ length: 6 }).map((__, j) => (
                  <TableCell key={j}>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : deals.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground py-10 text-center">
                {emptyMessage ?? 'No deals found.'}
              </TableCell>
            </TableRow>
          ) : (
            deals.map(d => (
              <TableRow key={d.id} className="cursor-pointer" onClick={() => onRowClick(d)}>
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell>
                  <Badge variant={stageVariant(d.stage)}>{stageLabel(d.stage)}</Badge>
                </TableCell>
                <TableCell>
                  <MoneyText amount={d.amount} currency={d.currency} visible={moneyVisible} />
                </TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell tabular-nums">
                  {Number(d.probability)}%
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {d.expected_close_date
                    ? new Date(d.expected_close_date).toLocaleDateString()
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  {d.is_archived ? (
                    <span className="inline-flex items-center justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Restore ${d.name}`}
                        onClick={e => {
                          e.stopPropagation();
                          onRestore(d);
                        }}
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                      <PurgeArchivedButton name={d.name} onPurge={() => onPurge(d)} />
                    </span>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Archive ${d.name}`}
                      onClick={e => {
                        e.stopPropagation();
                        onArchive(d);
                      }}
                    >
                      <Archive className="size-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function DealsList({ deals, onOpen, moneyVisible }: ViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {deals.map(d => (
        <li key={d.id}>
          <button
            type="button"
            onClick={() => onOpen(d)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <Badge variant={stageVariant(d.stage)} className="shrink-0">
              {stageLabel(d.stage)}
            </Badge>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{d.name}</span>
            <span className="hidden text-xs sm:inline">
              <MoneyText amount={d.amount} currency={d.currency} visible={moneyVisible} />
            </span>
            <span className="text-muted-foreground text-xs">
              {d.expected_close_date ? new Date(d.expected_close_date).toLocaleDateString() : '—'}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function DealsCards({ deals, onOpen, moneyVisible }: ViewProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {deals.map(d => (
        <Card
          key={d.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(d)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(d);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{d.name}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={stageVariant(d.stage)}>{stageLabel(d.stage)}</Badge>
            <span>
              <MoneyText amount={d.amount} currency={d.currency} visible={moneyVisible} />
            </span>
            <span className="text-muted-foreground ml-auto">
              {d.expected_close_date ? new Date(d.expected_close_date).toLocaleDateString() : '—'}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DealsGroupedByStage({ deals, onOpen, moneyVisible }: ViewProps) {
  // Bucket by stage, preserving DEAL_STAGES order (qualified → … → lost).
  const byStage = useMemo(() => {
    const map = new Map<string, Deal[]>();
    for (const stage of DEAL_STAGES) map.set(stage.value, []);
    for (const deal of deals) {
      const list = map.get(deal.stage) ?? [];
      list.push(deal);
      map.set(deal.stage, list);
    }
    return map;
  }, [deals]);

  return (
    <div className="space-y-6">
      {DEAL_STAGES.map(({ value, label }) => {
        const bucket = byStage.get(value) ?? [];
        if (bucket.length === 0) return null;
        // Deals don't snapshot base USD (see Deal model docstring) — sum per
        // currency rather than client-side FX conversion (C3 / F4).
        const totals = moneyVisible ? totalsByCurrency(bucket) : [];
        return (
          <section key={value} aria-labelledby={`stage-${value}`}>
            <h2
              id={`stage-${value}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
              {totals.length > 0 ? (
                <span className="text-foreground/80 ml-auto normal-case tracking-normal">
                  {formatCurrencyTotals(totals)}
                </span>
              ) : null}
            </h2>
            <DealsCards deals={bucket} onOpen={onOpen} moneyVisible={moneyVisible} />
          </section>
        );
      })}
    </div>
  );
}
